import React, { Fragment, useEffect, useState } from "react";
import { mergeRight } from "ramda";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import { lensProp, pick, set } from "ramda";
import Grid from "@material-ui/core/Grid";
import { FetchError } from "../../../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../../../auth/authActions";
import MuiAlert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import {
  useSftpAccountCreate,
  useSftpAccountUpdate,
} from "../sftpAccountServices";
import { isBlank, notBlank } from "../../../util/ext";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const initialState = {
  fields: {
    accountName: "",
    homeDirectory: "",
  },
};

const EditSftpAccount = ({ account, open, onClose, onSave }) => {
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (account && account.sftpAccountId) {
      setFields(pick(["accountName", "homeDirectory"], account));
    } else {
      setFields(initialState.fields);
    }
  }, [account]);

  const onFieldChanged = field => (event, optVal) => {
    const value = event.target.value || optVal;
    const update = set(lensProp(field), value, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const accountUpdate = useSftpAccountUpdate();
  const updateAccount = async acct => {
    console.log("Updating Account");
    await accountUpdate.mutateAsync({
      resourceIds: { sftpAccountId: acct.sftpAccountId },
      data: acct,
    });
  };

  const accountCreate = useSftpAccountCreate();
  const createAccount = async acct => {
    console.log("Creating Account");
    if (notBlank(acct.password) && acct.password === acct.confirmPassword) {
      await accountCreate.mutateAsync({ data: acct });
    } else {
      throw new Error("Confirm Password does not match.");
    }
  };

  const handleError = (message, err) => {
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const handleOnSave = async () => {
    const mergedAccount = mergeRight(account, fields);

    try {
      if (notBlank(mergedAccount.sftpAccountId)) {
        await updateAccount(mergedAccount);
      } else {
        await createAccount(mergedAccount);
      }

      onSave(mergedAccount);
    } catch (err) {
      handleError("Unable to save SFTP Account.", err);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      {account.sftpAccountId ? (
        <DialogTitle id="form-dialog-title">
          SFTP Account ({account.sftpAccountId})
        </DialogTitle>
      ) : (
        <DialogTitle id="form-dialog-title">New SFTP Account</DialogTitle>
      )}
      <DialogContent>
        <DialogContentText>Enter Account Information.</DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="accountName"
              label="Account Name"
              fullWidth
              value={fields.accountName || ""}
              onChange={onFieldChanged("accountName")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="homeDirectory"
              label="Home Directory"
              fullWidth
              value={fields.homeDirectory || ""}
              onChange={onFieldChanged("homeDirectory")}
            />
          </Grid>

          {isBlank(account.sftpAccountId) && (
            <Fragment>
              <Grid item xs={12}>
                <TextField
                  id="password"
                  label="Password"
                  type="password"
                  fullWidth
                  value={fields.password || ""}
                  onChange={onFieldChanged("password")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  value={fields.confirmPassword || ""}
                  onChange={onFieldChanged("confirmPassword")}
                />
              </Grid>
            </Fragment>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditSftpAccount;
